import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Search,
  HelpCircle,
  Settings,
  Menu,
  Briefcase,
  LayoutDashboard,
  ChevronDown,
  Building2,
  Shield
} from 'lucide-react';
import NotificationBell from './NotificationBell';
import UserDropdown from './UserDropdown';
import MobileNav from './MobileNav';
import useAuthStore from '../store/authStore';
import { useTenant } from '../contexts/TenantContext';
import { usePermissions } from '../hooks/usePermissions';

const Header = () => {
  const location = useLocation();
  const { user, isAuthenticated } = useAuthStore();
  const { tenant } = useTenant();
  const { canAccessAdminPanel } = usePermissions();
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Navigation items
  const navItems = [
    { label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard },
    { label: 'Find Work', path: '/jobs', icon: Briefcase },
    { label: 'Proposals', path: '/proposals' },
    { label: 'Messages', path: '/messages' },
    ...(canAccessAdminPanel() ? [{ label: 'Admin', path: '/admin', icon: Shield }] : []),
  ];

  // Check if current path matches nav item
  const isActiveRoute = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // Don't render header if not authenticated
  if (!isAuthenticated) {
    return null;
  }

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200 h-16 shadow-sm">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 h-full">
          <div className="flex items-center justify-between h-full">
            {/* Left section - Logo and Nav */}
            <div className="flex items-center space-x-8">
              {/* Mobile menu button */}
              <button
                onClick={() => setIsMobileNavOpen(true)}
                className="lg:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                aria-label="Open menu"
              >
                <Menu className="w-6 h-6" />
              </button>

              {/* Logo + Workspace */}
              <div className="flex items-center space-x-3">
                <Link
                  to="/dashboard"
                  className="flex items-center space-x-2"
                >
                  {tenant?.branding?.logo ? (
                    <img src={tenant.branding.logo} alt={tenant.name || 'Logo'} className="h-8 w-auto max-w-[120px] object-contain" />
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" fill="none" className="w-8 h-8">
                        <path d="M6 26L14 6L17 15L20 6L28 26L20 21L17 26L14 21Z" fill="#4F46E5" opacity="0.95"/>
                        <path d="M14 6L17 15L20 6L17 10Z" fill="#6366F1"/>
                      </svg>
                      <span className="hidden sm:block text-xl font-heading font-bold text-gray-900">
                        Propel
                      </span>
                    </>
                  )}
                </Link>
                {/* Workspace indicator */}
                {user?.role === 'owner' ? (
                  <span className="hidden sm:inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs font-medium">
                    <Shield className="w-3 h-3" />
                    Platform Admin
                  </span>
                ) : tenant?.name ? (
                  <span className="hidden sm:inline-flex items-center gap-1 px-2 py-0.5 bg-primary-50 text-primary-700 rounded text-xs font-medium max-w-[160px] truncate">
                    <Building2 className="w-3 h-3 flex-shrink-0" />
                    {tenant.name}
                  </span>
                ) : null}
              </div>

              {/* Desktop Navigation */}
              <nav className="hidden lg:flex items-center space-x-1">
                {navItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                      isActiveRoute(item.path)
                        ? 'text-primary-600 bg-primary-50'
                        : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    {item.label}
                  </Link>
                ))}
              </nav>
            </div>

            {/* Right section - Search, Icons, User */}
            <div className="flex items-center space-x-2 sm:space-x-4">
              {/* Search Bar - Hidden on mobile */}
              <div className="hidden md:flex items-center">
                <div className="relative">
                  <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-primary-500">
                    {/* Jobs dropdown */}
                    <div className="flex items-center px-3 py-2 bg-gray-50 border-r border-gray-300 cursor-pointer hover:bg-gray-100">
                      <span className="text-sm text-gray-600 mr-1">Jobs</span>
                      <ChevronDown className="w-4 h-4 text-gray-400" />
                    </div>
                    {/* Search input */}
                    <div className="flex items-center px-3">
                      <Search className="w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-32 lg:w-48 px-2 py-2 text-sm border-none focus:outline-none focus:ring-0"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Help Icon */}
              <button
                className="p-2 rounded-full text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
                aria-label="Help"
                title="Help Center"
              >
                <HelpCircle className="w-5 h-5" />
              </button>

              {/* Notifications */}
              <NotificationBell />

              {/* Settings Icon */}
              <Link
                to="/settings"
                className="p-2 rounded-full text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
                aria-label="Settings"
                title="Settings"
              >
                <Settings className="w-5 h-5" />
              </Link>

              {/* User Dropdown */}
              <UserDropdown user={user} />
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Navigation Drawer */}
      <MobileNav
        isOpen={isMobileNavOpen}
        onClose={() => setIsMobileNavOpen(false)}
        navItems={navItems}
        user={user}
      />
    </>
  );
};

export default Header;
